import React, { useRef, useState } from 'react';
import { Search } from '@americanexpress/dls-react';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import { withRouter } from '@americanexpress/one-app-router';
import PropTypes from 'prop-types';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getLastViewedProduct } from '@/selectors/lastViewedProductSelectors';
import { useTranslatedAirportResults } from '@/hooks/airportHooks/airportsHelperHooks';
import {
  useCommonContent,
  useOneCmsLocaleMinimumSearchLength,
  useOneCmsLocaleSearchObj,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { cn } from '@/helpers/utils';
import styles from './header/GlobalHeader.module.scss';
import AirportLinkWithImage from './AirportLinkWithImage';
import LFLink from './LFLink';
import ChevronLeft from '../icons/ChevronLeft';

const MAX_AIRPORT_RESULTS = 6;

const TCNSearchBox = ({ toggleFindAirportMenu }) => {
  const [query, setQuery] = useState('');
  const [routedAirport] = useState('');
  const lastViewedProduct = useSelector(getLastViewedProduct);
  const { locale } = useSelector(getLocaleData);
  const isUsMarket = !locale || locale.toLocaleLowerCase() === 'en-us';
  const { featuredAirports, header } = useCommonContent();
  const searchCopy = useOneCmsLocaleSearchObj();

  const { customTrack } = useAnalyticsContext();

  const inputRef = useRef(null);
  const airportsMatchingQuery = useTranslatedAirportResults(query);
  const minQueryLength = useOneCmsLocaleMinimumSearchLength();

  const onFocus = () => {
    if (query === routedAirport) {
      setQuery('');
    }
  };

  const onBlur = (e) => {
    if (query === '') {
      setQuery(routedAirport);
    }
    customTrack({ event: e, detail: query });
  };

  const handleOnChange = (event) => {
    setQuery(event.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      customTrack({
        event: e,
        element: 'header>>Find a City or Airport>>Search>>Type',
        detail: 'Press Enter',
      });
    }
  };

  const handleSearchButtonClick = (e) => {
    customTrack({
      event: e,
      element: 'header>>Find a City or Airport',
      detail: 'Search',
    });
  };

  const hasSearchQuery = query.length >= minQueryLength;
  const hasResults = airportsMatchingQuery.length > 0;
  const noResults = hasSearchQuery && !hasResults;

  const airportsList = hasSearchQuery
    ? airportsMatchingQuery.slice(0, MAX_AIRPORT_RESULTS)
    : featuredAirports.airports;
  const fitsInOneRow = airportsList.length <= MAX_AIRPORT_RESULTS / 2;

  let resultsHeading = featuredAirports.header;

  if (hasSearchQuery) {
    resultsHeading = hasResults
      ? `Results for "${query}"`
      : `No Results for "${query}"`;
  }

  return (
    <div className="tw-mx-auto tw-w-full tw-pl-4 tw-pr-0 md_tw-pl-6 md_tw-pr-6 lg_tw-max-w-360 lg_tw-px-8 xl_tw-px-10">
      <div className="tw-flex tw-flex-col tw-items-start tw-gap-6 tw-pb-10 tw-pt-6 md_tw-flex-row md_tw-justify-start md_tw-gap-6 lg_tw-gap-10">
        <div
          className={cn(
            styles.searchBar,
            'tw-w-full tw-shrink-0 tw-pr-4 md_tw-mb-0 md_tw-w-67 md_tw-pr-0 lg_tw-w-80'
          )}
        >
          <h1 className="tw-heading-xs-bold tw-pb-2 tw-font-bold tw-text-gray-300">
            {header.searchTabTitle}
          </h1>
          <Search
            id="findLounges"
            onBlur={onBlur}
            onFocus={onFocus}
            onClearSearch={() => setQuery('')}
            value={query || ''}
            containerRef={inputRef}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
            reactlytics={{ disable: true }}
            searchButtonProps={{
              onClick: handleSearchButtonClick,
              reactlytics: { disable: true },
            }}
            isOpen={false}
            placeholder={searchCopy.placeholder}
          />
        </div>
        <div className="tw-hidden tw-w-px tw-shrink-0 tw-self-stretch tw-bg-blue-deep-tint-700-t12 md_tw-flex" />
        <div className="tw-w-full tw-min-w-0">
          <h1
            className={cn('tw-heading-xs-bold tw-font-bold tw-text-gray-300', {
              'tw-pb-4': noResults,
            })}
          >
            {resultsHeading}
          </h1>
          {!noResults && (
            <ul
              className={cn(
                'scrollbar-hide tw-grid tw-snap-x tw-snap-mandatory tw-auto-cols-max tw-grid-flow-col tw-gap-x-6 tw-gap-y-6 tw-overflow-x-auto tw-overflow-y-hidden tw-overscroll-x-contain tw-scroll-smooth tw-py-4 lg_tw-snap-none lg_tw-auto-cols-auto lg_tw-grid-flow-row lg_tw-grid-cols-2 lg_tw-grid-rows-none lg_tw-overflow-visible xl_tw-grid-cols-3',
                fitsInOneRow ? 'tw-grid-rows-1' : 'tw-grid-rows-2'
              )}
            >
              {airportsList.map((airport, index) => {
                return (
                  <li
                    key={airport.iata}
                    className="tw-w-72 tw-shrink-0 tw-snap-start lg_tw-w-auto lg_tw-snap-none"
                  >
                    <AirportLinkWithImage
                      airport={airport}
                      position={index + 1}
                      background="dark"
                      onClickHandler={toggleFindAirportMenu}
                    />
                  </li>
                );
              })}
            </ul>
          )}
          {isUsMarket && (
            <div>
              <LFLink
                to={`${lastViewedProduct}/all-airports/`}
                className="tw-inline-flex tw-items-center tw-py-2dot5 tw-font-sans tw-text-base tw-font-medium tw-text-blue-bright-400"
                element="header>>Find a City or Airport>>Popular Airports"
                detail="View All Airports"
                onClickHandler={toggleFindAirportMenu}
              >
                {featuredAirports?.link}
                <ChevronLeft className="tw-blue-bright-400 tw-ml-1 tw-inline tw-h-3 tw-w-3 tw-rotate-180" />
              </LFLink>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const hocChain = compose(withRouter);

TCNSearchBox.propTypes = {
  toggleFindAirportMenu: PropTypes.func,
};

export default hocChain(TCNSearchBox);
